import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { APP_API_URL } from '../../../utils';
import OrderLayout from '../../../layout/order-layout';
import UIDropdown from '../../../components/ui/ui-dropdown';
import UICustomerDropdown from '../../../components/ui/ui-customer-dropdown';
import Add from '../../customers/add';
import Loader from '../../../components/loader';
import formatNumber, { calculateTaxItems } from '../../../utils/functions';

const SalesBrokenTiles = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [totalM2, setTotalM2] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState({ label: 'Sélectionner un client', value: '' });
  const [cart, setCart] = useState([]);
  const [selectedPaiement, setSelectedPaiement] = useState({ label: 'Sélectionner un moyen', value: '' });
  const [referencePiece, setReferencePiece] = useState('');
  const [remisePercentage, setRemisePercentage] = useState(0);
  const [remiseFixed, setRemiseFixed] = useState(0);
  const bagSizes = [1, 1.5, 1.75, 2];
  const [selectedAIBTax, setSelectedAIBTax] = useState({ label: 'AIB (0%)', value: null, emcf: null });
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const handleAIBTaxSelect = (option) => {
    setSelectedAIBTax(option);
  };

  const showReferencePiece = [3, 7].includes(selectedPaiement.value);
  const fetchProductsData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/broken-tiles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTotalM2(response.data.total_m2_disponible);
    } catch (error) {
      console.error('Erreur lors de la récupération des variants :', error);
    } finally {
      setLoading(false);
    }
  };
  const fetchCustomers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { per_page: 500, sort_by: 'name', sort_direction: 'asc' },
      });
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des clients :', error);
    }
  }, []);
  useEffect(() => {
    fetchProductsData();
    fetchCustomers();
  }, [fetchCustomers]);
  useEffect(() => {
    const savedCart = localStorage.getItem('brokenCart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);
  useEffect(() => {
    localStorage.setItem('brokenCart', JSON.stringify(cart));
  }, [cart]);
  const currentCartM2 = cart.reduce((acc, p) => acc + (p.quantity * p.bagSize || 0), 0);
  const remainingM2 = totalM2 - currentCartM2;
  const addBag = (size) => {
    if (remainingM2 < size) {
      Swal.fire({ icon: 'info', text: 'Pas assez de quantité disponible pour ajouter ce sac.' });
      return;
    }
    setCart(prevCart => {
      const existing = prevCart.find(p => p.bagSize === size);
      if (existing) {
        return prevCart.map(p =>
          p.uniqueId === existing.uniqueId ? { ...p, quantity: p.quantity + 1 } : p
        );
      } else {
        return [
          ...prevCart,
          {
            uniqueId: Date.now(),
            name: `Sac de ${size} m² de carreaux cassés`,
            sku: `BROKEN-${size}`,
            bagSize: size,
            quantity: 1,
            customPrice: null,
          },
        ];
      }
    });
  };

  const removeFromCart = (uniqueId) => {
    setCart(cart.filter((p) => p.uniqueId !== uniqueId));
  };
  const handleQuantityChange = (e, product) => {
    let newQuantity = parseInt(e.target.value);
    if (isNaN(newQuantity) || newQuantity < 1) newQuantity = 1;
    const otherTotal = cart.reduce((acc, p) => p.uniqueId !== product.uniqueId ? acc + (p.quantity * p.bagSize) : 0, 0);
    const maxQuantity = Math.floor((totalM2 - otherTotal) / product.bagSize);
    if (newQuantity > maxQuantity) newQuantity = maxQuantity;
    setCart(
      cart.map((p) =>
        p.uniqueId === product.uniqueId ? { ...p, quantity: newQuantity } : p
      )
    );
  };
  const updateProductPrice = (uniqueId, newPrice) => {
    if (isNaN(newPrice)) newPrice = null;
    setCart(
      cart.map((p) =>
        p.uniqueId === uniqueId ? { ...p, customPrice: newPrice } : p
      )
    );
  };
  const calculateNetAmount = () => {
    return cart.reduce(
      (acc, product) =>
        acc + (product.customPrice || 0) * (product.quantity * product.bagSize || 0),
      0
    );
  };
  const calculateRemise = () => {
    return remiseFixed + (remisePercentage / 100) * calculateNetAmount();
  };
  const calculateTotal = () => {
    return calculateNetAmount() - calculateRemise();
  };
  const handleCustomersSelect = (option) => {
    setSelectedCustomer(option);
  };
  const handlePaiementSelect = (option) => {
    setSelectedPaiement(option);
  };
  const handleAddCustomer = (newCustomer) => {
    setCustomers([...customers, newCustomer]);
    setSelectedCustomer({
      label: newCustomer.name,
      value: newCustomer.id,
      phone: newCustomer.numero,
      client_code: newCustomer.client_code,
      email: newCustomer.email,
      customer: newCustomer,
    });
  };
  const handleSubmit = async () => {
    if (loading) return;
    const totalQ = cart.reduce((acc, p) => acc + (p.quantity * p.bagSize), 0);
    if (totalQ > totalM2) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'La quantité totale demandée dépasse la quantité disponible.',
      });
      return;
    }
    if (cart.some(p => !p.customPrice || p.customPrice <= 0)) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Veuillez saisir un prix valide pour le produit.',
      });
      return;
    }
    setLoading(true);
    const aibRate = selectedAIBTax.emcf === "A" ? 0.01 : selectedAIBTax.emcf === "B" ? 0.05 : null;
    const remise = calculateRemise();
    // Prix unitaire = FCFA/m² ; quantité fiscale = m² vendus (sacs × format)
    const items = cart.map((e) => ({
      name: `${e.bagSize} (B)`,
      price: Number(e.customPrice),
      quantity: e.quantity * e.bagSize,
      taxGroup: 'B',
    }));
    const result = calculateTaxItems(items, aibRate, remise);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${APP_API_URL}/broken-tiles/sell`,
        {
          items: cart.map((p) => ({
            broken_tile_id: p.bagSize,
            quantity: p.quantity,
            unit_price: p.customPrice,
          })),
          customer_id: selectedCustomer.value,
          payment_method: selectedPaiement.value,
          discount: calculateRemise(),
          note: referencePiece,
          aib: result.totals.totalAIB,
          aib_value: selectedAIBTax.emcf ?? null,
          tva: result.totals.totalTVA,
          montant_ht: result.totals.montantHT,
          montant_net: result.totals.finalTTC,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // 🔹 Récupérer le ref depuis la réponse API
      const ref = response.data.sale.id;

      Swal.fire({
        icon: 'success',
        title: 'Succès',
        text: 'Commande validée avec succès!',
      }).then(() => {
        // 🔹 Redirection vers la route dynamique avec le ref reçu de l'API
        navigate(`/broken-tiles/validate/${ref}`);
      });
      setCart([]);
      localStorage.removeItem('brokenCart');
      setSelectedCustomer({ label: 'Sélectionner un client', value: '' });
      setSelectedPaiement({ label: 'Sélectionner un moyen', value: '' });
      setReferencePiece('');
      setRemisePercentage(0);
      setRemiseFixed(0);
      fetchProductsData();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || 'Erreur lors de la validation de la commande',
      });
    } finally {
      setLoading(false);
    }
  };
  // const handleWheel = (e) => e.target.blur();
  // const handleKeyDown = (e) => {
  //   if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
  // };
  const canSubmit =
    selectedPaiement.value &&
    calculateTotal() > 0 &&
    selectedCustomer?.value &&
    (!showReferencePiece || referencePiece) &&
    !loading;

  if (loading && totalM2 === 0 && cart.length === 0) {
    return (
      <OrderLayout>
        <Loader />
      </OrderLayout>
    );
  }

  return (
    <OrderLayout>
      <div className="duo-caisse-layout">
        <div className="duo-caisse-products-col">
          <div className="duo-caisse-products-header">
            <div className="duo-caisse-toolbar">
              <Link to="/admin/sales-broken-tiles/list" className="duo-btn duo-btn--ghost duo-btn--sm">
                <i className="bi bi-list-ul" /> Historique
              </Link>
            </div>
            <h2 className="duo-caisse-title">Carreaux cassés</h2>
            <p className="duo-caisse-subtitle">
              Stock disponible : <strong>{remainingM2.toFixed(2)} m²</strong>
              {currentCartM2 > 0 && (
                <span> — panier : {currentCartM2.toFixed(2)} m²</span>
              )}
            </p>
          </div>

          <div className="duo-caisse-products-scroll">
            {remainingM2 < 1 ? (
              <div className="text-center py-5">
                <img src="/assets/img/no-product.png" width={280} height={280} alt="Aucun stock" />
                <p className="mt-3 text-muted">Aucun m² disponible à la vente.</p>
              </div>
            ) : (
              <div className="duo-product-grid">
                {bagSizes
                  .filter((size) => remainingM2 >= size)
                  .map((size) => (
                    <div
                      key={`bag-${size}`}
                      role="button"
                      tabIndex={0}
                      className="duo-product-card"
                      onClick={() => addBag(size)}
                      onKeyDown={(e) => e.key === 'Enter' && addBag(size)}
                    >
                      <p className="duo-product-card__name">{`Sac de ${size} m²`}</p>
                      <p className="duo-product-card__sku">{`BROKEN-${size}`}</p>
                      <div className="duo-product-card__meta">
                        <span className="duo-product-card__stock">{size} m² / sac</span>
                        <span className="duo-product-card__price">
                          {Math.floor(remainingM2 / size)} sac(s)
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        <div className="duo-caisse-cart-col">
          <div className="duo-cart-panel">
            <div className="duo-cart-panel__inner">
              <div className="duo-cart-panel__head">
                <h2>
                  Commande
                  <span className="duo-cart-count">{cart.length}</span>
                </h2>
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost duo-btn--sm"
                  style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }}
                  onClick={() => {
                    setCart([]);
                    localStorage.removeItem('brokenCart');
                    setSelectedCustomer({ label: 'Sélectionner un client', value: '' });
                  }}
                >
                  <i className="bi bi-x" /> Vider
                </button>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="duo-form-label mb-0">Client</label>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost duo-btn--sm"
                    title="Ajouter un client"
                    onClick={() => setShowAddCustomerModal(true)}
                  >
                    <i className="bi bi-plus" /> Nouveau
                  </button>
                </div>
                <UICustomerDropdown
                  options={customers.map((item) => ({
                    label: item.name,
                    value: item.id,
                    phone: item.numero,
                    client_code: item.client_code,
                    email: item.email,
                    customer: item,
                  }))}
                  selectedOption={selectedCustomer}
                  onSelect={handleCustomersSelect}
                  enableSearch
                />
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                {cart.length === 0 ? (
                  <div className="text-center py-3">
                    <img src="/assets/img/no-product.png" width={160} height={160} alt="Panier vide" />
                    <p className="small text-muted mt-2 mb-0">Ajoutez des sacs depuis la gauche.</p>
                  </div>
                ) : (
                  cart.map((product) => (
                    <div className="duo-cart-item" key={product.uniqueId}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="duo-cart-item__name">{product.name}</span>
                        <button
                          type="button"
                          title="Supprimer"
                          className="duo-btn duo-btn--ghost duo-btn--sm"
                          onClick={() => removeFromCart(product.uniqueId)}
                        >
                          <i className="bi bi-trash" style={{ color: '#ff4b4b' }} />
                        </button>
                      </div>
                      <div className="row g-2">
                        <div className="col-6">
                          <label className="duo-form-label">Nombre de sacs</label>
                          <input
                            type="number"
                            className="duo-input"
                            min={1}
                            step={1}
                            value={product.quantity}
                            onChange={(e) => handleQuantityChange(e, product)}
                          />
                        </div>
                        <div className="col-6">
                          <label className="duo-form-label">Prix / m²</label>
                          <input
                            type="number"
                            className="duo-input"
                            min={0}
                            value={product.customPrice ?? ''}
                            placeholder="Prix"
                            onChange={(e) => {
                              const newPrice = parseFloat(e.target.value);
                              updateProductPrice(product.uniqueId, newPrice);
                            }}
                          />
                        </div>
                      </div>
                      <p className="mb-0 mt-2" style={{ fontWeight: 800, fontSize: '0.85rem', color: '#006633' }}>
                        {formatNumber((product.customPrice || 0) * (product.quantity * product.bagSize || 0))} FCFA
                      </p>
                    </div>
                  ))
                )}
              </div>

              <div className="duo-cart-totals">
                <div className="row g-2 mb-2">
                  <div className="col-12">
                    <label className="duo-form-label">Type d&apos;impôt AIB</label>
                    <UIDropdown
                      options={[
                        { label: 'AIB (0%)', value: null, emcf: null },
                        { label: 'AIB (01%)', value: 0.01, emcf: 'A' },
                        { label: 'AIB (05%)', value: 0.05, emcf: 'B' },
                      ]}
                      selectedOption={selectedAIBTax}
                      onSelect={handleAIBTaxSelect}
                      enableSearch
                    />
                  </div>
                  <div className="col-6">
                    <label className="duo-form-label">Remise (%)</label>
                    <input
                      type="number"
                      className="duo-input"
                      min={0}
                      max={100}
                      value={remisePercentage}
                      onChange={(e) => setRemisePercentage(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div className="col-6">
                    <label className="duo-form-label">Remise fixe</label>
                    <input
                      type="number"
                      className="duo-input"
                      min={0}
                      value={remiseFixed}
                      onChange={(e) => setRemiseFixed(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>

                <table>
                  <tbody>
                    {remisePercentage > 0 && (
                      <tr>
                        <td style={{ color: '#ff4b4b' }}>Remise ({remisePercentage}%)</td>
                        <td style={{ color: '#ff4b4b' }}>
                          {formatNumber((remisePercentage / 100) * calculateNetAmount())} FCFA
                        </td>
                      </tr>
                    )}
                    {remiseFixed > 0 && (
                      <tr>
                        <td style={{ color: '#ff4b4b' }}>Remise fixe</td>
                        <td style={{ color: '#ff4b4b' }}>{formatNumber(remiseFixed)} FCFA</td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ color: '#ff4b4b' }}>Remise totale</td>
                      <td style={{ color: '#ff4b4b' }}>{formatNumber(calculateRemise())} FCFA</td>
                    </tr>
                    <tr className="duo-total-row">
                      <td>Total</td>
                      <td>{formatNumber(calculateTotal())} FCFA</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-3">
                <label className="duo-form-label">Méthode de paiement</label>
                <UIDropdown
                  options={[
                    { label: 'Mobile Money', value: 1 },
                    { label: 'Carte bancaire', value: 2 },
                    { label: 'Virement', value: 3 },
                    { label: 'Espèces', value: 4 },
                    { label: 'Crédit', value: 5 },
                    { label: 'Chèques', value: 7 },
                    { label: 'Autre', value: 6 },
                  ]}
                  selectedOption={selectedPaiement}
                  onSelect={handlePaiementSelect}
                  enableSearch
                />
                {showReferencePiece && (
                  <div className="mt-2">
                    <label htmlFor="referencePiece" className="duo-form-label">
                      Référence pièce
                    </label>
                    <input
                      type="text"
                      className="duo-input"
                      id="referencePiece"
                      value={referencePiece}
                      onChange={(e) => setReferencePiece(e.target.value)}
                      placeholder="Référence de la pièce"
                    />
                  </div>
                )}
              </div>

              <div className="mt-3 pt-3" style={{ borderTop: '2px solid #f0f0f0' }}>
                <button
                  type="button"
                  className="duo-btn duo-btn--primary w-100"
                  style={!canSubmit ? { opacity: 0.5, cursor: 'not-allowed', boxShadow: 'none' } : undefined}
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  {loading ? 'Validation...' : `Valider — ${formatNumber(calculateTotal())} FCFA`}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Add
        open={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onAddCustomer={handleAddCustomer}
        refreshData={fetchCustomers}
      />
    </OrderLayout>
  );
};
export default SalesBrokenTiles;